import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  HeadingNode,
  QuoteNode,
  $isHeadingNode,
  $createHeadingNode,
  $createQuoteNode,
} from "@lexical/rich-text";
import {
  ListNode,
  ListItemNode,
  $isListNode,
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import {
  LinkNode,
  TOGGLE_LINK_COMMAND,
  $isLinkNode,
} from "@lexical/link";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  EditorState,
  LexicalEditor,
  FORMAT_TEXT_COMMAND,
  $createParagraphNode,
  UNDO_COMMAND,
  REDO_COMMAND,
} from "lexical";
import { $setBlocksType } from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";

// ─── Theme ────────────────────────────────────────────────────────────────────
const editorTheme = {
  paragraph: "mb-3 leading-relaxed text-navy/80",
  heading: {
    h1: "text-2xl font-bold text-navy mb-4 mt-6 first:mt-0",
    h2: "text-xl font-bold text-navy mb-3 mt-5 first:mt-0",
    h3: "text-lg font-semibold text-navy mb-2 mt-4 first:mt-0",
  },
  list: {
    nested: { listitem: "list-none" },
    ol: "list-decimal pl-6 mb-3 space-y-1",
    ul: "list-disc pl-6 mb-3 space-y-1",
    listitem: "text-navy/80",
    listitemChecked: "line-through text-navy/40",
    listitemUnchecked: "text-navy/80",
  },
  quote: "border-l-4 border-gold pl-4 italic text-navy/60 my-4",
  text: {
    bold: "font-bold",
    italic: "italic",
    underline: "underline",
    strikethrough: "line-through",
    underlineStrikethrough: "underline line-through",
  },
  link: "text-gold underline underline-offset-2 cursor-pointer hover:opacity-70 transition-opacity",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isHtmlContent(str: string): boolean {
  return /<[a-z][\s\S]*>/i.test(str);
}

function sanitizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^(https?|mailto|tel):\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

// ─── Initial HTML loader plugin ───────────────────────────────────────────────
function InitialHtmlPlugin({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext();
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !html) return;
    loaded.current = true;

    editor.update(() => {
      const root = $getRoot();
      if (root.getFirstChild() !== null && root.getTextContent().trim()) return;

      const htmlStr = isHtmlContent(html)
        ? html
        : html
            .split("\n")
            .filter(Boolean)
            .map((p) => `<p>${p}</p>`)
            .join("");

      const parser = new DOMParser();
      const dom = parser.parseFromString(htmlStr, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      root.clear();
      if (nodes.length > 0) {
        root.append(...nodes);
      }
    });
  }, [editor, html]);

  return null;
}

// ─── Link Popup ───────────────────────────────────────────────────────────────
interface LinkPopupProps {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  initialUrl: string;
  isEditing: boolean;
  onSave: (url: string) => void;
  onRemove: () => void;
  onClose: () => void;
}

function LinkPopup({ anchorRef, initialUrl, isEditing, onSave, onRemove, onClose }: LinkPopupProps) {
  const [url, setUrl] = useState(initialUrl);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [anchorRef]);

  const handleSave = () => {
    const clean = sanitizeUrl(url);
    if (!clean) return;
    onSave(clean);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!pos) return null;

  return createPortal(
    <div
      style={{ position: "absolute", top: pos.top, left: pos.left, zIndex: 9999 }}
      className="bg-white border border-cool-gray-200 rounded-lg shadow-xl p-3 w-80"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <p className="text-xs font-semibold text-navy/60 uppercase tracking-wider mb-2">
        {isEditing ? "Edit Link" : "Insert Link"}
      </p>
      <div className="flex gap-2 mb-2">
        <input
          ref={inputRef}
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="https://example.com"
          className="flex-1 px-3 py-2 text-sm text-navy bg-cool-gray-50 border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!url.trim()}
          className="flex-1 px-3 py-1.5 text-xs font-semibold text-white bg-navy rounded-md hover:bg-navy/80 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isEditing ? "Update" : "Insert Link"}
        </button>
        {isEditing && (
          <button
            type="button"
            onClick={onRemove}
            className="px-3 py-1.5 text-xs font-semibold text-red-500 border border-red-200 rounded-md hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Remove
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center text-navy/40 hover:text-navy hover:bg-cool-gray-100 rounded transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>
      <p className="text-xs text-navy/35 mt-2">
        Press Enter to confirm · Esc to cancel
      </p>
    </div>,
    document.body
  );
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────
type BlockType = "paragraph" | "h1" | "h2" | "h3" | "bullet" | "number" | "quote";

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<BlockType>("paragraph");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Link state
  const [isLink, setIsLink] = useState(false);
  const [currentLinkUrl, setCurrentLinkUrl] = useState("");
  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const linkBtnRef = useRef<HTMLButtonElement>(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    setIsBold(selection.hasFormat("bold"));
    setIsItalic(selection.hasFormat("italic"));
    setIsUnderline(selection.hasFormat("underline"));

    const anchorNode = selection.anchor.getNode();

    // Detect link
    const parent = anchorNode.getParent();
    if ($isLinkNode(parent)) {
      setIsLink(true);
      setCurrentLinkUrl(parent.getURL());
    } else if ($isLinkNode(anchorNode)) {
      setIsLink(true);
      setCurrentLinkUrl((anchorNode as typeof parent & { getURL: () => string }).getURL?.() ?? "");
    } else {
      setIsLink(false);
      setCurrentLinkUrl("");
    }

    const element =
      anchorNode.getKey() === "root"
        ? anchorNode
        : anchorNode.getTopLevelElementOrThrow();

    if ($isListNode(element)) {
      const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
      const type = parentList ? parentList.getListType() : element.getListType();
      setBlockType(type === "bullet" ? "bullet" : "number");
    } else if ($isHeadingNode(element)) {
      setBlockType(element.getTag() as BlockType);
    } else {
      setBlockType(element.getType() as BlockType);
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(updateToolbar);
      }),
      editor.registerCommand(UNDO_COMMAND, () => { setCanUndo(false); return false; }, 1),
      editor.registerCommand(REDO_COMMAND, () => { setCanRedo(false); return false; }, 1)
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerUpdateListener(({ tags }) => {
      if (tags.has("history-merge") || tags.has("skip-scroll-into-view")) return;
      setCanUndo(true);
    });
  }, [editor]);

  // Close popup on outside click
  useEffect(() => {
    if (!showLinkPopup) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (linkBtnRef.current && !linkBtnRef.current.contains(target)) {
        setShowLinkPopup(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLinkPopup]);

  const handleLinkButtonClick = () => {
    setShowLinkPopup((v) => !v);
  };

  const handleInsertLink = (url: string) => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url, target: "_blank", rel: "noopener noreferrer" });
    setShowLinkPopup(false);
  };

  const handleRemoveLink = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    setShowLinkPopup(false);
    setIsLink(false);
    setCurrentLinkUrl("");
  };

  const applyHeading = (tag: "h1" | "h2" | "h3") => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (blockType === tag) $setBlocksType(selection, () => $createParagraphNode());
        else $setBlocksType(selection, () => $createHeadingNode(tag));
      }
    });
  };

  const applyList = (type: "bullet" | "number") => {
    if ((type === "bullet" && blockType === "bullet") || (type === "number" && blockType === "number")) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else if (type === "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  const applyQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (blockType === "quote") $setBlocksType(selection, () => $createParagraphNode());
        else $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const btnBase = "w-8 h-8 flex items-center justify-center rounded text-sm transition-colors cursor-pointer";
  const btnNormal = `${btnBase} text-navy/50 hover:text-navy hover:bg-cool-gray-100`;
  const btnActive = `${btnBase} text-navy bg-navy/10`;
  const divider = "w-px h-5 bg-cool-gray-200 mx-1";

  const blockLabels: Record<string, string> = {
    paragraph: "Paragraph",
    h1: "Heading 1",
    h2: "Heading 2",
    h3: "Heading 3",
    bullet: "Bullet List",
    number: "Numbered List",
    quote: "Quote",
  };

  return (
    <div className="flex items-center gap-0.5 flex-wrap px-3 py-2 border-b border-cool-gray-200 bg-cool-gray-50 rounded-t-md">
      {/* Undo / Redo */}
      <button
        type="button"
        disabled={!canUndo}
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        className={canUndo ? btnNormal : `${btnBase} text-navy/20 cursor-not-allowed`}
        title="Undo"
      >
        <i className="ri-arrow-go-back-line" />
      </button>
      <button
        type="button"
        disabled={!canRedo}
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        className={canRedo ? btnNormal : `${btnBase} text-navy/20 cursor-not-allowed`}
        title="Redo"
      >
        <i className="ri-arrow-go-forward-line" />
      </button>

      <div className={divider} />

      {/* Block type */}
      <select
        value={blockType}
        onChange={(e) => {
          const val = e.target.value as BlockType;
          if (val === "h1" || val === "h2" || val === "h3") applyHeading(val);
          else if (val === "bullet") applyList("bullet");
          else if (val === "number") applyList("number");
          else if (val === "quote") applyQuote();
          else {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () => $createParagraphNode());
              }
            });
          }
        }}
        className="h-8 px-2 text-xs font-medium text-navy bg-white border border-cool-gray-200 rounded cursor-pointer focus:outline-none hover:border-navy/30 transition-colors"
      >
        {Object.entries(blockLabels).map(([val, label]) => (
          <option key={val} value={val}>{label}</option>
        ))}
      </select>

      <div className={divider} />

      {/* Text format */}
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
        className={isBold ? btnActive : btnNormal}
        title="Bold (Ctrl+B)"
      >
        <i className="ri-bold" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
        className={isItalic ? btnActive : btnNormal}
        title="Italic (Ctrl+I)"
      >
        <i className="ri-italic" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
        className={isUnderline ? btnActive : btnNormal}
        title="Underline (Ctrl+U)"
      >
        <i className="ri-underline" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")}
        className={btnNormal}
        title="Strikethrough"
      >
        <i className="ri-strikethrough" />
      </button>

      <div className={divider} />

      {/* Lists */}
      <button
        type="button"
        onClick={() => applyList("bullet")}
        className={blockType === "bullet" ? btnActive : btnNormal}
        title="Bullet list"
      >
        <i className="ri-list-unordered" />
      </button>
      <button
        type="button"
        onClick={() => applyList("number")}
        className={blockType === "number" ? btnActive : btnNormal}
        title="Numbered list"
      >
        <i className="ri-list-ordered" />
      </button>

      {/* Quote */}
      <button
        type="button"
        onClick={applyQuote}
        className={blockType === "quote" ? btnActive : btnNormal}
        title="Blockquote"
      >
        <i className="ri-double-quotes-l" />
      </button>

      <div className={divider} />

      {/* Link */}
      <div className="relative">
        <button
          ref={linkBtnRef}
          type="button"
          onClick={handleLinkButtonClick}
          className={isLink || showLinkPopup ? btnActive : btnNormal}
          title={isLink ? "Edit link" : "Insert link"}
        >
          <i className="ri-link" />
        </button>

        {showLinkPopup && (
          <LinkPopup
            anchorRef={linkBtnRef}
            initialUrl={currentLinkUrl}
            isEditing={isLink}
            onSave={handleInsertLink}
            onRemove={handleRemoveLink}
            onClose={() => setShowLinkPopup(false)}
          />
        )}
      </div>
    </div>
  );
}

// ─── HTML Export Plugin ────────────────────────────────────────────────────────
interface HtmlExportPluginProps {
  onChange: (html: string) => void;
}

function HtmlExportPlugin({ onChange }: HtmlExportPluginProps) {
  const handleChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor, null);
        onChange(html);
      });
    },
    [onChange]
  );
  return <OnChangePlugin onChange={handleChange} />;
}

// ─── Main RichTextEditor ───────────────────────────────────────────────────────
interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const EDITOR_NODES = [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode];

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Write the full article here…",
  minHeight = "240px",
}: RichTextEditorProps) {
  const initialConfig = {
    namespace: "ArticleBodyEditor",
    nodes: EDITOR_NODES,
    theme: editorTheme,
    onError: (error: Error) => console.error("[Lexical]", error),
  };

  return (
    <div className="border border-cool-gray-200 rounded-md overflow-hidden focus-within:border-navy transition-colors">
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div className="relative bg-white">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="w-full px-4 py-4 text-sm text-navy focus:outline-none"
                style={{ minHeight }}
                aria-placeholder={placeholder}
              />
            }
            placeholder={
              <div
                className="absolute top-4 left-4 text-sm text-navy/30 pointer-events-none select-none"
                style={{ userSelect: "none" }}
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={() => (
              <div className="p-4 text-red-500 text-sm">
                Editor error. Please reload.
              </div>
            )}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <InitialHtmlPlugin html={value} />
        <HtmlExportPlugin onChange={onChange} />
      </LexicalComposer>
    </div>
  );
}
